/* rpdRender.ts — nerdle rebus puzzle renderer (ported from /rebus builder/rpd_render.js).
 * Draws an RPD (Rebus Puzzle Descriptor) onto a canvas: brand-font text, icons from our own
 * library (served at /rebus/icons/svg/), the renderer primitives, a bespoke patternText effect,
 * and a light brand attribution border + optional wordmark.
 *
 * KEEP IN SYNC with c:/scripts phd/rebus/builder/rpd_render.js — that file is the source of
 * truth for the renderer; this is the labs snapshot. Only ICON_DIR + the module wrapper differ.
 */
export type RPDElement = Record<string, unknown> & { type?: string }
export type RPD = {
  id?: string
  width?: number
  height?: number
  background?: string
  valign?: 'top' | 'bottom'
  halign?: 'left' | 'right'
  elements?: RPDElement[]
}
export type RenderOpts = {
  border?: boolean
  wordmark?: boolean
  pad?: number
  maxScale?: number
  borderColor?: string
  borderAlpha?: number
  borderWidth?: number
}

const INK = '#161803', MUTED = '#989484', ACCENT = '#820458'
// Icons are served from public/rebus/icons/svg/. In production the app is hosted under /labs/,
// so prefix with PUBLIC_URL (empty in dev) — a bare "/rebus/..." would 404 on the deployed site.
const ICON_DIR = (process.env.PUBLIC_URL || '') + '/rebus/icons/svg/'

const isUpper = (t: string) => /[A-Z]/.test(t) && t === t.toUpperCase()
const FAM: Record<string, string> = { serif: 'Georgia', script: 'Caveat', hand: 'Caveat', condensed: 'Oswald', impact: 'Impact' }
function fontFor(el: any): string {
  const caps = el.case === 'upper' || (el.text && isUpper(el.text))
  const size = el.fontSize || 40
  const weight = el.weight || (caps ? 300 : 500)
  const family = el.family ? (FAM[el.family] || el.family) : (caps ? 'Montserrat' : 'Barlow')
  return `${weight} ${size}px "${family}", Arial, sans-serif`
}

// --- image cache: fetch SVG text once, optionally tint (non-white fill -> hex), -> Image ---
const cache: Record<string, Promise<HTMLImageElement>> = {}
const WHITE = new Set(['rgb(255,255,255)', 'rgb(255, 255, 255)', '#ffffff', '#fff', 'white'])
async function loadIcon(slug: string, tint?: string): Promise<HTMLImageElement> {
  const key = slug + '|' + (tint || '')
  if (key in cache) return cache[key]
  let txt = await fetch(ICON_DIR + slug + '.svg').then((r) => { if (!r.ok) throw new Error('404 ' + slug); return r.text() })
  if (tint) txt = txt.replace(/fill="([^"]+)"/g, (m, v) => (WHITE.has(String(v).trim().toLowerCase()) ? m : `fill="${tint}"`))
  const img = new Image()
  const p = new Promise<HTMLImageElement>((res, rej) => { img.onload = () => res(img); img.onerror = rej })
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(txt)
  cache[key] = p
  return p
}

function renderText(ctx: CanvasRenderingContext2D, el: any) {
  if (el.spans) {   // one continuous word with per-segment colour (e.g. "moonceon", moon letters blue)
    ctx.save(); ctx.font = fontFor(el); ctx.textBaseline = 'middle'; ctx.textAlign = 'left'
    const parts = el.spans.map((s: any) => ({ t: (el.case === 'upper' ? s.t.toUpperCase() : el.case === 'lower' ? s.t.toLowerCase() : s.t), c: s.color }))
    const total = parts.reduce((a: number, p: any) => a + ctx.measureText(p.t).width, 0)
    let x = el.x - total / 2
    for (const p of parts) { ctx.fillStyle = p.c || el.color || INK; ctx.fillText(p.t, x, el.y); x += ctx.measureText(p.t).width }
    ctx.restore(); return
  }
  ctx.save()
  ctx.font = fontFor(el); ctx.fillStyle = el.color || INK
  ctx.textAlign = el.align || 'center'; ctx.textBaseline = 'middle'
  let t = el.text || ''
  if (el.case === 'upper') t = t.toUpperCase(); if (el.case === 'lower') t = t.toLowerCase()
  ctx.translate(el.x, el.y)
  if (el.rotation) ctx.rotate((el.rotation * Math.PI) / 180)
  if (el.outline) { ctx.fillStyle = '#fff'; ctx.fillText(t, 0, 0); ctx.strokeStyle = el.color || INK; ctx.lineWidth = el.outlineWidth || 2.5; ctx.strokeText(t, 0, 0) }
  else ctx.fillText(t, 0, 0)
  ctx.restore()
}

// Big glyphs whose strokes are FILLED with a repeating little-text texture (e.g. "SIS" made of "oa").
function renderPatternText(ctx: CanvasRenderingContext2D, el: any) {
  const W = el.w || 300, H = el.h || 150, SS = 2
  const off = document.createElement('canvas'); off.width = W * SS; off.height = H * SS
  const o = off.getContext('2d')!; o.scale(SS, SS)
  o.fillStyle = el.color || INK; o.textAlign = 'center'; o.textBaseline = 'middle'
  const ps = el.patternSize || 15
  o.font = `700 ${ps}px "Barlow", Arial, sans-serif`
  for (let yy = ps * 0.7; yy < H; yy += ps * 0.92)
    for (let xx = ps; xx < W + ps; xx += ps * 1.5) o.fillText(el.pattern || 'oa', xx, yy)
  o.globalCompositeOperation = 'destination-in'
  let t = el.text || ''; if (el.case === 'upper') t = t.toUpperCase()
  o.font = `900 ${el.fontSize || 130}px "Montserrat", Arial, sans-serif`
  o.fillStyle = '#000'; o.fillText(t, W / 2, H / 2)
  ctx.drawImage(off, el.x - W / 2, el.y - H / 2, W, H)
}

function loadRaw(url: string): Promise<HTMLImageElement> {   // external image (e.g. the original Re-Bus PNG)
  const img = new Image(); img.crossOrigin = 'anonymous'
  const p = new Promise<HTMLImageElement>((res, rej) => { img.onload = () => res(img); img.onerror = rej })
  img.src = url; return p
}
async function renderImage(ctx: CanvasRenderingContext2D, el: any) {
  let img: HTMLImageElement
  try { img = el.url ? await loadRaw(el.url) : await loadIcon(el.source, el.tint) }
  catch (e) { placeholder(ctx, el); return }
  const w = el.w || 80, h = el.h || 80, a = el.anchor || 'center'
  let x = el.x, y = el.y
  if (a.includes('center') || a === 'center') { x -= w / 2; y -= h / 2 }
  if (a.includes('right')) x -= w
  if (a.includes('bottom')) y -= h
  ctx.save()
  if (el.rotation) { ctx.translate(el.x, el.y); ctx.rotate((el.rotation * Math.PI) / 180); ctx.translate(-el.x, -el.y) }
  if (el.flip === 'h') { ctx.translate(x + w, y); ctx.scale(-1, 1); ctx.drawImage(img, 0, 0, w, h) }
  else if (el.cropBottom) { ctx.beginPath(); ctx.rect(x, y, w, h * (1 - el.cropBottom)); ctx.clip(); ctx.drawImage(img, x, y, w, h) }
  else ctx.drawImage(img, x, y, w, h)
  ctx.restore()
}
function placeholder(ctx: CanvasRenderingContext2D, el: any) {
  const w = el.w || 80, h = el.h || 80
  ctx.save(); ctx.strokeStyle = MUTED; ctx.setLineDash([4, 4]); ctx.strokeRect(el.x - w / 2, el.y - h / 2, w, h)
  ctx.setLineDash([]); ctx.fillStyle = MUTED; ctx.font = '12px Arial'; ctx.textAlign = 'center'
  ctx.fillText('[' + (el.source || '?') + ']', el.x, el.y); ctx.restore()
}

function roughLine(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, j?: number) {
  j = j || 2; const seg = Math.max(2, Math.round(Math.hypot(x2 - x1, y2 - y1) / 18))
  ctx.beginPath(); ctx.moveTo(x1, y1)
  for (let i = 1; i <= seg; i++) { const t = i / seg; ctx.lineTo(x1 + (x2 - x1) * t + (Math.random() - 0.5) * j, y1 + (y2 - y1) * t + (Math.random() - 0.5) * j) }
  ctx.stroke()
}

function drawPrimitive(ctx: CanvasRenderingContext2D, el: any) {
  ctx.save(); ctx.strokeStyle = el.color || INK; ctx.fillStyle = el.color || INK
  ctx.lineWidth = el.width || 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round'
  const { x, y, w = 80, h = 80 } = el
  switch (el.kind) {
    case 'groundline': roughLine(ctx, x - w / 2, y, x + w / 2, y, 3); break
    case 'connector': roughLine(ctx, el.x1, el.y1, el.x2, el.y2, 1.5); break
    case 'square':
      if (el.fill === false) ctx.strokeRect(x - w / 2, y - h / 2, w, h)
      else ctx.fillRect(x - w / 2, y - h / 2, w, h); break
    case 'ring': ctx.lineWidth = el.width || 3; ctx.beginPath(); ctx.ellipse(x, y, w / 2, h / 2, 0, 0, Math.PI * 2); ctx.stroke(); break
    case 'scatter': {
      const n = el.count || 14
      for (let i = 0; i < n; i++) { ctx.beginPath(); ctx.arc(x - w / 2 + Math.random() * w, y - h / 2 + Math.random() * h, el.size || 2, 0, Math.PI * 2); ctx.fill() }
      break }
    case 'starburst': {
      const n = el.count || 8, r0 = el.inner || w * 0.15, r1 = w / 2
      for (let i = 0; i < n; i++) { const a = (i / n) * Math.PI * 2; roughLine(ctx, x + Math.cos(a) * r0, y + Math.sin(a) * r0, x + Math.cos(a) * r1, y + Math.sin(a) * r1, 1) }
      break }
    case 'stinklines': {
      const n = el.count || 3
      for (let i = 0; i < n; i++) {
        const ox = x + (i - (n - 1) / 2) * (el.gap || 16)
        ctx.beginPath(); ctx.moveTo(ox, y + h / 2)
        ctx.bezierCurveTo(ox - 11, y + h / 6, ox + 11, y - h / 6, ox - 7, y - h / 2)
        ctx.stroke()
      }
      break }
    case 'ibeam': {
      const bh = h * 0.14, sw = w * 0.5, L = x - w / 2, R = x + w / 2, TP = y - h / 2, BT = y + h / 2, sl = x - sw / 2, sr = x + sw / 2
      ctx.beginPath()
      ctx.moveTo(L, TP); ctx.lineTo(R, TP); ctx.lineTo(R, TP + bh); ctx.lineTo(sr, TP + bh)
      ctx.lineTo(sr, BT - bh); ctx.lineTo(R, BT - bh); ctx.lineTo(R, BT); ctx.lineTo(L, BT)
      ctx.lineTo(L, BT - bh); ctx.lineTo(sl, BT - bh); ctx.lineTo(sl, TP + bh); ctx.lineTo(L, TP + bh)
      ctx.closePath(); ctx.stroke(); break }
    case 'dot':
      ctx.beginPath(); ctx.arc(x, y, el.r || w / 2, 0, Math.PI * 2); ctx.fill(); break
  }
  ctx.restore()
}

function drawBorder(ctx: CanvasRenderingContext2D, W: number, H: number, opts: RenderOpts) {
  if (opts.border !== false) {
    ctx.save(); ctx.strokeStyle = opts.borderColor || ACCENT; ctx.globalAlpha = opts.borderAlpha ?? 0.55
    ctx.lineWidth = opts.borderWidth || 3
    const r = 14, i = 9
    ctx.beginPath()
    ctx.moveTo(i + r, i); ctx.arcTo(W - i, i, W - i, H - i, r); ctx.arcTo(W - i, H - i, i, H - i, r)
    ctx.arcTo(i, H - i, i, i, r); ctx.arcTo(i, i, W - i, i, r); ctx.closePath(); ctx.stroke(); ctx.restore()
  }
  if (opts.wordmark) {
    ctx.save(); ctx.textAlign = 'right'; ctx.textBaseline = 'alphabetic'
    const rx = W - 24, by = H - 12
    ctx.font = '400 17px "Times New Roman", serif'; ctx.fillStyle = '#b4b0a9'
    ctx.fillText('.', rx, by)
    const dotW = ctx.measureText('.').width
    ctx.font = '300 15px "Quicksand", Arial'; ctx.fillStyle = MUTED
    ctx.fillText('nerdle', rx - dotW, by)
    ctx.restore()
  }
}

function bboxOf(ctx: CanvasRenderingContext2D, el: any) {
  if (el.type === 'text') {
    ctx.save(); ctx.font = fontFor(el)
    let w: number, h = (el.fontSize || 40) * 1.05
    if (el.spans) { w = el.spans.reduce((a: number, s: any) => a + ctx.measureText(el.case === 'upper' ? s.t.toUpperCase() : s.t).width, 0); ctx.restore(); return { minX: el.x - w / 2, maxX: el.x + w / 2, minY: el.y - h / 2, maxY: el.y + h / 2 } }
    let t = el.text || ''; if (el.case === 'upper') t = t.toUpperCase(); if (el.case === 'lower') t = t.toLowerCase()
    w = ctx.measureText(t).width; ctx.restore()
    const a = el.align || 'center'
    const x0 = a === 'left' ? el.x : a === 'right' ? el.x - w : el.x - w / 2
    return { minX: x0, maxX: x0 + w, minY: el.y - h / 2, maxY: el.y + h / 2 }
  }
  if (el.type === 'patternText') {
    const w = el.w || 240, h = el.h || 140
    return { minX: el.x - w / 2, maxX: el.x + w / 2, minY: el.y - h / 2, maxY: el.y + h / 2 }
  }
  if (el.type === 'image') {
    let w = el.w || 80, h = el.h || 80
    if (el.rotation && Math.abs(el.rotation) % 180 === 90) { const t = w; w = h; h = t }
    const a = el.anchor || 'center'
    let x = el.x, y = el.y
    if (a.includes('center') || a === 'center') { x -= w / 2; y -= h / 2 }
    if (a.includes('right')) x -= w; if (a.includes('bottom')) y -= h
    return { minX: x, maxX: x + w, minY: y, maxY: y + h * (el.cropBottom ? 1 - el.cropBottom : 1) }
  }
  if (el.type === 'line' || (el.type === 'primitive' && el.kind === 'connector')) {
    return { minX: Math.min(el.x1, el.x2), maxX: Math.max(el.x1, el.x2), minY: Math.min(el.y1, el.y2), maxY: Math.max(el.y1, el.y2) }
  }
  const w = el.w || 80, h = el.h || (el.kind === 'groundline' ? 6 : 80)
  return { minX: el.x - w / 2, maxX: el.x + w / 2, minY: el.y - h / 2, maxY: el.y + h / 2 }
}

export async function renderRPD(canvas: HTMLCanvasElement, rpd: RPD, opts: RenderOpts = {}) {
  const W = rpd.width || 480, H = rpd.height || 320, PAD = opts.pad ?? 22
  canvas.width = W; canvas.height = H
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = rpd.background || '#ffffff'; ctx.fillRect(0, 0, W, H)
  const els = [...(rpd.elements || [])].sort((a: any, b: any) => (a.zIndex || 0) - (b.zIndex || 0))

  let bb: { minX: number; minY: number; maxX: number; maxY: number } | null = null
  for (const el of els) { const b = bboxOf(ctx, el); if (!bb) bb = { ...b }; else { bb.minX = Math.min(bb.minX, b.minX); bb.minY = Math.min(bb.minY, b.minY); bb.maxX = Math.max(bb.maxX, b.maxX); bb.maxY = Math.max(bb.maxY, b.maxY) } }
  ctx.save()
  if (bb) {
    const cw = bb.maxX - bb.minX || 1, ch = bb.maxY - bb.minY || 1
    const availW = W - 2 * PAD, availH = H - 2 * PAD
    const scale = Math.min(availW / cw, availH / ch, opts.maxScale ?? 2.4)
    const cwid = (bb.maxX - bb.minX) * scale, chgt = (bb.maxY - bb.minY) * scale
    const tx = rpd.halign === 'right' ? W - PAD - cwid / 2 : rpd.halign === 'left' ? PAD + cwid / 2 : W / 2
    const ty = rpd.valign === 'top' ? PAD + chgt / 2 : rpd.valign === 'bottom' ? H - PAD - chgt / 2 : H / 2
    ctx.translate(tx, ty); ctx.scale(scale, scale); ctx.translate(-(bb.minX + bb.maxX) / 2, -(bb.minY + bb.maxY) / 2)
  }
  for (const el of els as any[]) {
    if (el.type === 'text') renderText(ctx, el)
    else if (el.type === 'patternText') renderPatternText(ctx, el)
    else if (el.type === 'image') await renderImage(ctx, el)
    else if (el.type === 'primitive') drawPrimitive(ctx, el)
    else if (el.type === 'line') { ctx.save(); ctx.strokeStyle = el.color || INK; ctx.lineWidth = el.width || 3; ctx.lineCap = 'round'; roughLine(ctx, el.x1, el.y1, el.x2, el.y2, el.jitter ?? 2); ctx.restore() }
  }
  ctx.restore()
  drawBorder(ctx, W, H, opts)
}
